import * as styles from "./AmendmentHistoryDialog.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Loader } from "@frontend/components/atoms/Loader";
import { Trans, useLingui } from "@lingui/react/macro";

import { useAmendmentHistory } from "../hooks/useAmendmentHistory";

export type AmendmentHistoryDialogProps = {
  /** The amendment to tell the story of; null keeps the dialog closed. */
  amendmentId: number | null;
  onClose: () => void;
};

/** dd.mm.yyyy hh:mm, from the ISO instant the API answers. */
function moment(iso: string): string {
  const date = new Date(iso);

  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/**
 * The life of one amendment. Kept apart from the list on purpose: the
 * collaborateur screen will show the same thing once it becomes React.
 */
export function AmendmentHistoryDialog({
  amendmentId,
  onClose,
}: AmendmentHistoryDialogProps): JSX.Element | null {
  const { t } = useLingui();
  const { history, loading, error } = useAmendmentHistory(amendmentId);

  if (null === amendmentId) {
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent size="lg" aria-label={t`Amendment history`}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Amendment history</Trans>
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          {null === history ? null : (
            <p className={styles.subject}>
              {history.employeeName} — {history.year}
            </p>
          )}

          {loading ? <Loader /> : null}

          {null === error ? null : <p role="alert">{error}</p>}

          {null !== history && 0 === history.entries.length && !loading ? (
            <p className={styles.muted}>
              <Trans>Nothing happened to this amendment yet.</Trans>
            </p>
          ) : null}

          {null !== history && history.entries.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.header}>
                    <Trans>When</Trans>
                  </th>
                  <th className={styles.header}>
                    <Trans>Who</Trans>
                  </th>
                  <th className={styles.header}>
                    <Trans>Change</Trans>
                  </th>
                  <th className={styles.header}>
                    <Trans>Comment</Trans>
                  </th>
                </tr>
              </thead>

              <tbody>
                {history.entries.map((entry, index) => (
                  <tr key={`${entry.changedAt}-${index}`}>
                    <td className={styles.cell}>{moment(entry.changedAt)}</td>

                    <td className={styles.cell}>
                      {entry.changedBy ?? (
                        <span className={styles.muted}>
                          <Trans>The application</Trans>
                        </span>
                      )}
                    </td>

                    <td className={styles.cell}>
                      {null === entry.previousStatusLabel ? (
                        <Trans>Generated</Trans>
                      ) : entry.previousStatus === entry.newStatus ? (
                        <span className={styles.muted}>
                          <Trans>No change of status</Trans>
                        </span>
                      ) : (
                        `${entry.previousStatusLabel} → ${entry.newStatusLabel}`
                      )}
                    </td>

                    <td className={styles.cell}>{entry.comment ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={onClose}>
            <Trans>Close</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
